"use client";

import { useState, useEffect, useMemo } from "react";
import { trpc } from "@/lib/trpc";
// import { DataTable } from "@/components/data-table"; // DataTable will no longer be used directly for rendering
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type PaginationState,
} from "@tanstack/react-table";
// import { DataTablePagination } from "@/components/data-table-pagination"; // Removed as component is deleted
import { getActivationCodeColumns, type UserActivationCode, type ActivationCodeActionHandlers } from "./columns";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckSquare, ArrowUpDown, Globe, MoreHorizontal, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, ShoppingCart } from "lucide-react"; // Added Search
import { useTranslation } from "react-i18next";
import { useDebounce } from "@/hooks/use-debounce"; // Added useDebounce
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox"; // Added for skeleton header
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const UserActivationCodesPageSkeleton = ({ pageSize, columnDefinitions }: { pageSize: number; columnDefinitions: { id: string; headerLabel: string; skeletonWidthClass: string; isMono?: boolean, isBadge?: boolean, isActions?: boolean }[] }) => {
  return Array.from({ length: pageSize }).map((_, rowIndex) => (
    <TableRow key={`skeleton-row-${rowIndex}`}>
      {columnDefinitions.map((col, colIndex) => (
        <TableCell key={`skeleton-cell-${rowIndex}-${col.id}`} className="py-3">
          {col.id === 'select' ? (
            <Skeleton className="h-5 w-5 rounded" />
          ) : col.isActions ? (
            <div className="flex justify-end">
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          ) : col.isBadge ? (
             <Skeleton className={`h-6 ${col.skeletonWidthClass} rounded-full`} />
          ) : (
            <Skeleton className={`h-5 ${col.skeletonWidthClass} rounded ${col.isMono ? 'font-mono' : ''}`} />
          )}
        </TableCell>
      ))}
    </TableRow>
  ));
};


export default function UserActivationCodesPage() {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [rowSelection, setRowSelection] = useState({}); // For table selection state
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showUnbindConfirm, setShowUnbindConfirm] = useState(false);
  const [showBindDomainDialog, setShowBindDomainDialog] = useState(false);
  const [selectedCodeId, setSelectedCodeId] = useState<string | null>(null);
  const [selectedBindingId, setSelectedBindingId] = useState<string | null>(null);
  const [domainToBind, setDomainToBind] = useState("");

  const utils = trpc.useUtils();

  const codesQuery = trpc.userActivation.listUserCodes.useQuery(
    { page: pagination.pageIndex + 1, pageSize: pagination.pageSize, searchTerm: debouncedSearchTerm || undefined },
    { placeholderData: (previousData) => previousData}
  );

  useEffect(() => {
    // Reset pageIndex to 0 when search term changes
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [debouncedSearchTerm]);

  // useEffect(() => {
  //   console.log("codesQuery status:", {
  //     isLoading: codesQuery.isLoading,
  //     isFetching: codesQuery.isFetching,
  //     isError: codesQuery.isError,
  //     error: codesQuery.error,
  //     data: codesQuery.data,
  //   });
  // }, [codesQuery.data, codesQuery.isLoading, codesQuery.isFetching, codesQuery.isError, codesQuery.error]);

  const resetCodeMutation = trpc.userActivation.resetCode.useMutation({
    onSuccess: (data) => {
      toast.success(t("userActivationCodes.resetSuccess", { code: data.code, defaultValue: `Code ${data.code} has been reset successfully.` }));
      utils.userActivation.listUserCodes.invalidate();
      setShowResetConfirm(false);
      setSelectedCodeId(null);
    },
    onError: (error) => {
      toast.error(t("userActivationCodes.resetError", { message: error.message, defaultValue: `Failed to reset code: ${error.message}` }));
      setShowResetConfirm(false);
      setSelectedCodeId(null);
    },
  });

  const bindDomainMutation = trpc.userActivation.bindDomain.useMutation({
    onSuccess: (data) => {
      toast.success(t("userActivationCodes.bindSuccess", { domain: data.bindingValue, defaultValue: `Domain ${data.bindingValue} bound successfully to code.` }));
      utils.userActivation.listUserCodes.invalidate();
      setShowBindDomainDialog(false);
      setSelectedCodeId(null);
      setDomainToBind("");
    },
    onError: (error) => {
      toast.error(t("userActivationCodes.bindError", { message: error.message, defaultValue: `Failed to bind domain: ${error.message}` }));
    },
  });

  const unbindDomainMutation = trpc.userActivation.unbindDomain.useMutation({
    onSuccess: (data) => {
      toast.success(t("userActivationCodes.unbindSuccess", { bindingId: data.unbindedBindingId, defaultValue: `Domain unbinding successful for binding ID: ${data.unbindedBindingId}.` }));
      utils.userActivation.listUserCodes.invalidate();
      setShowUnbindConfirm(false);
      setSelectedCodeId(null);
      setSelectedBindingId(null);
    },
    onError: (error) => {
      toast.error(t("userActivationCodes.unbindError", { message: error.message, defaultValue: `Failed to unbind domain: ${error.message}` }));
      setShowUnbindConfirm(false);
      setSelectedCodeId(null);
      setSelectedBindingId(null);
    },
  });

  const actionHandlers: ActivationCodeActionHandlers = {
    onResetCode: (codeId) => {
      setSelectedCodeId(codeId);
      setShowResetConfirm(true);
    },
    onBindDomain: (codeId) => {
      setSelectedCodeId(codeId);
      setShowBindDomainDialog(true);
    },
    onUnbindDomain: (codeId, bindingId) => {
      setSelectedCodeId(codeId);
      setSelectedBindingId(bindingId);
      setShowUnbindConfirm(true);
    },
  };

  const columns = useMemo(() => getActivationCodeColumns(actionHandlers, t), [actionHandlers, t]);

  const skeletonColumnDefinitions = useMemo(() => [
    { id: "select", headerLabel: "", skeletonWidthClass: "w-5", isSelect: true },
    { id: "code", headerLabel: t("userActivationCodes.columns.code", "Code"), skeletonWidthClass: "w-28", isMono: true }, // Adjusted width
    { id: "product", headerLabel: t("userActivationCodes.columns.product", "Product"), skeletonWidthClass: "w-36" }, // Adjusted width
    { id: "status", headerLabel: t("userActivationCodes.columns.status", "Status"), skeletonWidthClass: "w-20", isBadge: true }, // Adjusted width
    { id: "bindings", headerLabel: t("userActivationCodes.columns.boundDomains", "Bound Domains"), skeletonWidthClass: "w-52" }, // Adjusted width
    { id: "expiresAt", headerLabel: t("userActivationCodes.columns.expiresAt", "Expires At"), skeletonWidthClass: "w-28" }, // Adjusted width
    { id: "actions", headerLabel: t("userActivationCodes.columns.actions", "Actions"), skeletonWidthClass: "w-8", isActions: true },
  ], [t]);


  const handleResetConfirm = () => {
    if (selectedCodeId) {
      resetCodeMutation.mutate({ activationCodeId: selectedCodeId });
    }
  };

  const handleBindDomainSubmit = () => {
    if (selectedCodeId && domainToBind) {
      bindDomainMutation.mutate({ activationCodeId: selectedCodeId, domainName: domainToBind });
    } else {
      toast.error(t("userActivationCodes.enterDomainError", "Please enter a domain name."));
    }
  };

  const handleUnbindConfirm = () => {
    if (selectedCodeId && selectedBindingId) {
      unbindDomainMutation.mutate({ activationCodeId: selectedCodeId, bindingId: selectedBindingId });
    }
  };

  const pageCount = codesQuery.data ? Math.ceil(codesQuery.data.total / pagination.pageSize) : 0;

  const table = useReactTable({
    data: (codesQuery.data?.items ?? []) as UserActivationCode[],
    columns,
    state: {
      pagination,
      rowSelection,
    },
    pageCount: pageCount,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderTableArea = () => {
    if (codesQuery.isFetching && !codesQuery.isError && !codesQuery.data) { // Show skeleton only on initial load or full refetch
      return (
        <div className="bg-card rounded-md border shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 border-b">
                {skeletonColumnDefinitions.map(col => (
                  <TableHead key={`skeleton-header-${col.id}`} className="font-medium py-4">
                    {col.id === 'select' ? <Checkbox disabled className="opacity-50" /> : col.headerLabel}
                    {/* For sortable columns, you might add ArrowUpDown here if not handled by column def */}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <UserActivationCodesPageSkeleton pageSize={pagination.pageSize} columnDefinitions={skeletonColumnDefinitions} />
            </TableBody>
          </Table>
        </div>
      );
    }

    if (codesQuery.isError) {
      return (
        <div className="py-10 text-center">
          <p className="text-destructive mb-4">{t("common.errorPrefix", "Error")}: {codesQuery.error.message}</p>
          <Button onClick={() => codesQuery.refetch()}>{t("common.tryAgain", "Try Again")}</Button>
        </div>
      );
    }

    if (!table.getRowModel().rows?.length) {
      return (
        <div className="bg-card rounded-md border shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
               <TableRow className="bg-muted/50 border-b">
                {columns.map(column => ( // Use actual columns for header structure
                  <TableHead key={`no-results-header-${column.id || (typeof column.header === 'string' ? column.header : '')}`} className="font-medium py-4">
                     { typeof column.header === 'function' ? flexRender(column.header, { table } as any) : column.header }
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="border-b hover:bg-muted/50">
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {t("userActivationCodes.noResults", "No activation codes found.")}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      );
    }

    // Render actual table data
    return (
      <div className="bg-card rounded-md border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id} className="bg-muted/50">
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id} className="font-medium py-4">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map(row => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className="hover:bg-muted/50"
              >
                {row.getVisibleCells().map(cell => (
                  <TableCell key={cell.id} className="py-3">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="container mx-auto py-10">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">{t("userActivationCodes.title", "My Activation Codes")}</h1>
          <p className="text-muted-foreground mt-2">
            {t("userActivationCodes.description", "Manage your activation codes, bind them to domains, and view their status.")}
          </p>
        </div>
        <div>
          <Button asChild>
            <a href="/order" target="_blank" rel="noopener noreferrer">
              <ShoppingCart className="mr-2 h-4 w-4" />
              {t("userActivationCodes.buyButtonLabel", "Buy Activation Code")}
            </a>
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder={t("userActivationCodes.searchPlaceholder", "Search by code, product name...")}
            className="w-full pl-10 pr-4 py-2 text-sm rounded-md border"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {/* Placeholder for future filters */}
        {/*
        <Select>
          <SelectTrigger className="w-[180px] sm:w-[200px] text-sm rounded-md border">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder={t("userActivationCodes.filterStatusPlaceholder", "Filter by status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("statusFilters.all", "All Statuses")}</SelectItem>
            <SelectItem value="active">{t("userActivationCodeStatus.active", "Active")}</SelectItem>
            <SelectItem value="used">{t("userActivationCodeStatus.used", "Used")}</SelectItem>
            <SelectItem value="expired">{t("userActivationCodeStatus.expired", "Expired")}</SelectItem>
          </SelectContent>
        </Select>
        */}
      </div>

      <div className="space-y-6">
        {renderTableArea()}
      </div>

      {(pageCount > 0 || (codesQuery.data?.items && codesQuery.data.items.length > 0)) && !codesQuery.isError && (
        <div className="flex items-center justify-between mt-6 px-1">
          <div className="flex-1 text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length} {t("common.of")} {table.getFilteredRowModel().rows.length} {t("common.rowsSelected", { count: table.getFilteredSelectedRowModel().rows.length})}
          </div>
          <div className="flex items-center space-x-2 sm:space-x-6 lg:space-x-8">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium hidden sm:inline">
                {t("common.rowsPerPage")}
              </span>
              <Select
                value={`${pagination.pageSize}`}
                onValueChange={(value) => {
                  const newPageSize = Number(value);
                  setPagination(prev => ({ ...prev, pageIndex: 0, pageSize: newPageSize }));
                  table.setPageSize(newPageSize);
                  table.setPageIndex(0);
                }}
              >
                <SelectTrigger className="h-8 w-16 sm:w-20">
                  <SelectValue placeholder={pagination.pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 20, 30, 40, 50].map((size) => (
                    <SelectItem key={size} value={`${size}`}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-[100px] sm:w-[120px] items-center justify-center text-sm font-medium">
              {t("common.page")} {pagination.pageIndex + 1} {t("common.of")} {pageCount || 1}
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => { setPagination(prev => ({ ...prev, pageIndex: 0 })); table.setPageIndex(0); }}
                disabled={pagination.pageIndex === 0}
              >
                <span className="sr-only">{t("common.firstPage")}</span>
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => { setPagination(prev => ({ ...prev, pageIndex: prev.pageIndex - 1})); table.previousPage(); }}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">{t("common.previousPage")}</span>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => { setPagination(prev => ({ ...prev, pageIndex: prev.pageIndex + 1})); table.nextPage(); }}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">{t("common.nextPage")}</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => { setPagination(prev => ({ ...prev, pageIndex: (pageCount || 1) -1 })); table.setPageIndex((pageCount || 1) -1); }}
                disabled={pagination.pageIndex === (pageCount || 1) - 1 || pageCount === 0}
              >
                <span className="sr-only">{t("common.lastPage")}</span>
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dialogs remain unchanged */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("userActivationCodes.resetConfirmTitle", "Are you sure you want to reset this code?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("userActivationCodes.resetConfirmDescription", "This action will generate a new activation code. The old code will no longer be valid. Any existing domain bindings will remain. This action cannot be undone.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedCodeId(null)}>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetConfirm} disabled={resetCodeMutation.isPending}>
              {resetCodeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("userActivationCodes.actions.reset", "Reset Code")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showBindDomainDialog} onOpenChange={(isOpen) => {
        setShowBindDomainDialog(isOpen);
        if (!isOpen) {
          setSelectedCodeId(null);
          setDomainToBind("");
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("userActivationCodes.bindDomainTitle", "Bind Domain")}</DialogTitle>
            <DialogDescription>
              {t("userActivationCodes.bindDomainDescription", "Enter the domain name you want to bind to this activation code. Make sure the domain format is correct (e.g., example.com).")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="domainName" className="text-right">
                {t("userActivationCodes.domainLabel", "Domain")}
              </Label>
              <Input
                id="domainName"
                value={domainToBind}
                onChange={(e) => setDomainToBind(e.target.value)}
                className="col-span-3"
                placeholder={t('placeholders.domainExample')}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t("common.cancel", "Cancel")}</Button>
            </DialogClose>
            <Button onClick={handleBindDomainSubmit} disabled={bindDomainMutation.isPending}>
              {bindDomainMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("userActivationCodes.actions.bindDomain", "Bind Domain")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showUnbindConfirm} onOpenChange={setShowUnbindConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("userActivationCodes.unbindConfirmTitle", "Are you sure you want to unbind this domain?")}</AlertDialogTitle>
            <AlertDialogDescription>
             {t("userActivationCodes.unbindConfirmDescription", "This action will remove the domain binding from the activation code. This action cannot be undone.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setSelectedCodeId(null); setSelectedBindingId(null); }}>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnbindConfirm} disabled={unbindDomainMutation.isPending}>
              {unbindDomainMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("userActivationCodes.actions.unbindDomain", "Unbind Domain")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}